import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GENDERS, PLAYER_COLORS, updateProfileSchema, type Gender, type MeResponse, type UpdateProfileInput } from '@rw/shared';
import { useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { Avatar, Button, Card } from '../../components/ui';
import { qk } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { MY_COLOR } from '../../lib/colors';
import { countries } from '../../lib/countries';
import { squarePhoto } from '../../lib/resize-image';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { PhoneInput } from './PhoneInput';
import { useT } from '../../i18n';

const GENDER_LABEL: Record<Gender, string> = { male: 'Male', female: 'Female', other: 'Other' };
const input = 'h-12 w-full rounded-xl border border-ink-700 bg-ink-850 px-4 outline-none focus:border-volt';

interface Form {
  displayName: string;
  color: string;
  bio: string;
  gender: string;
  birthday: string;
  heightCm: string;
  weightKg: string;
  country: string;
  address: string;
  /** E.164, null when empty, '' when what was typed is not a valid number. */
  phone: string | null;
}

function formOf(u: MeResponse): Form {
  const p = u.profile;
  return {
    displayName: u.displayName,
    color: u.color,
    bio: p?.bio ?? '',
    gender: p?.gender ?? '',
    birthday: p?.birthday ?? '',
    heightCm: p?.heightCm?.toString() ?? '',
    weightKg: p?.weightKg?.toString() ?? '',
    country: p?.country ?? '',
    address: p?.address ?? '',
    phone: p?.phone ?? null,
  };
}

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

/** Only the fields that changed, in API shape. */
function changesOf(f: Form, was: Form): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.displayName.trim() !== was.displayName) out.displayName = f.displayName;
  if (f.color !== was.color) out.color = f.color;
  if (f.bio.trim() !== was.bio) out.bio = f.bio;
  if (f.gender !== was.gender) out.gender = f.gender || null;
  if (f.birthday !== was.birthday) out.birthday = f.birthday || null;
  if (f.heightCm.trim() !== was.heightCm) out.heightCm = num(f.heightCm);
  if (f.weightKg.trim() !== was.weightKg) out.weightKg = num(f.weightKg);
  if (f.country !== was.country) out.country = f.country || null;
  if (f.address.trim() !== was.address) out.address = f.address;
  if (f.phone !== was.phone) out.phone = f.phone;
  return out;
}

/** Name, photo, colour, health and contact details. */
export function EditProfilePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const online = useNetwork((s) => s.online);
  const [initial] = useState(() => (user ? formOf(user) : null));
  const [f, setF] = useState<Form | null>(initial);
  const fileInput = useRef<HTMLInputElement>(null);
  const t = useT();
  const set = <K extends keyof Form>(k: K) => (v: Form[K]) => setF((cur) => (cur ? { ...cur, [k]: v } : cur));

  const saved = (u: MeResponse) => {
    qc.setQueryData(['me', 'profile'], u);
    setUser(u);
    void qc.invalidateQueries({ queryKey: qk.myStats });
    void qc.invalidateQueries({ queryKey: ['players', u.id] });
    void qc.invalidateQueries({ queryKey: ['leaderboard'] });
  };
  const failed = (title: string) => (e: Error) => toast({ kind: 'error', title: t(title), body: e instanceof ApiError ? e.message : undefined });

  const save = useMutation({
    mutationFn: (body: UpdateProfileInput) => api<MeResponse>('/me/profile', { method: 'PATCH', body }),
    onSuccess: (u) => {
      saved(u);
      toast({ kind: 'success', title: t('Profile saved') });
      navigate('/profile');
    },
    onError: failed('Could not save profile'),
  });
  const photo = useMutation({
    mutationFn: async (file: File | null) =>
      file
        ? api<MeResponse>('/me/avatar', { method: 'PUT', body: { image: await squarePhoto(file) } })
        : api<MeResponse>('/me/avatar', { method: 'DELETE' }),
    onSuccess: saved,
    onError: failed('Could not update photo'),
  });

  if (!user || !f || !initial) return null;
  const changes = changesOf(f, initial);
  const dirty = Object.keys(changes).length > 0;
  const check = dirty ? updateProfileSchema.safeParse(changes) : null;
  const issues = new Map<string, string>();
  if (check && !check.success) for (const i of check.error.issues) if (!issues.has(String(i.path[0]))) issues.set(String(i.path[0]), t(i.message));
  if (f.phone === '') issues.set('phone', t('Enter a valid phone number'));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {t('‹ Back')}
      </button>
      <h1 className="font-display text-3xl font-bold">{t('Edit profile')}</h1>

      <div className="mt-5 flex flex-col items-center">
        <Avatar name={f.displayName || user.displayName} color={MY_COLOR} size="lg" src={user.avatarUrl} />
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" size="sm" loading={photo.isPending} disabled={!online} onClick={() => fileInput.current?.click()} data-testid="change-photo">
            {t(user.avatarUrl ? 'Change photo' : 'Add photo')}
          </Button>
          {user.avatarUrl ? (
            <Button variant="ghost" size="sm" disabled={!online || photo.isPending} onClick={() => photo.mutate(null)} data-testid="remove-photo">
              {t('Remove')}
            </Button>
          ) : null}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="photo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) photo.mutate(file);
          }}
        />
      </div>

      <Card className="mt-5 space-y-4">
        <Field label={t('Name')} error={issues.get('displayName')}>
          <input value={f.displayName} onChange={(e) => set('displayName')(e.target.value)} maxLength={32} autoComplete="nickname" className={input} data-testid="display-name-input" />
        </Field>
        <Field label={t('Bio')} hint={t('{n}/150 · shown on your public profile', { n: f.bio.length })} error={issues.get('bio')}>
          <textarea value={f.bio} onChange={(e) => set('bio')(e.target.value)} maxLength={150} rows={3} className={`${input} h-auto py-3`} data-testid="bio-input" />
        </Field>
        <div>
          <div className="text-sm font-semibold">{t('Colour')}</div>
          <p className="text-xs text-ink-400">{t('How other runners see you on the map. You always see yourself in green.')}</p>
          <div className="mt-3 grid grid-cols-6 gap-3" role="radiogroup" aria-label={t('Colour')}>
            {PLAYER_COLORS.map((c) => (
              <button
                key={c}
                role="radio"
                aria-checked={f.color === c}
                aria-label={c}
                onClick={() => set('color')(c)}
                className={`hex-clip grid aspect-square w-full place-items-center transition ${f.color === c ? 'scale-110 opacity-100' : 'opacity-60'}`}
                style={{ background: c }}
              >
                {f.color === c ? <span className="text-lg font-bold text-ink-950">✓</span> : null}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <h2 className="mt-6 font-display text-xl font-bold">{t('Health')}</h2>
      <p className="text-xs text-ink-400">{t('Private — only you can see this.')}</p>
      <Card className="mt-2 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Gender')}>
            <select value={f.gender} onChange={(e) => set('gender')(e.target.value)} className={input} data-testid="gender-input">
              <option value="">—</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>
                  {t(GENDER_LABEL[g])}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Birthday')} error={issues.get('birthday')}>
            <input type="date" value={f.birthday} max={today} min="1900-01-01" onChange={(e) => set('birthday')(e.target.value)} className={`${input} min-w-0`} data-testid="birthday-input" />
          </Field>
          <Field label={t('Height (cm)')} error={issues.get('heightCm')}>
            <input type="number" inputMode="numeric" value={f.heightCm} onChange={(e) => set('heightCm')(e.target.value)} className={input} data-testid="height-input" />
          </Field>
          <Field label={t('Weight (kg)')} error={issues.get('weightKg')}>
            <input type="number" inputMode="decimal" step="0.1" value={f.weightKg} onChange={(e) => set('weightKg')(e.target.value)} className={input} data-testid="weight-input" />
          </Field>
        </div>
      </Card>

      <h2 className="mt-6 font-display text-xl font-bold">{t('Contact')}</h2>
      <p className="text-xs text-ink-400">{t('Private — only you can see this.')}</p>
      <Card className="mt-2 space-y-4">
        <Field label={t('Country')}>
          <select value={f.country} onChange={(e) => set('country')(e.target.value)} className={input} data-testid="country-input">
            <option value="">—</option>
            {countries().map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('Address')} error={issues.get('address')}>
          <input value={f.address} onChange={(e) => set('address')(e.target.value)} maxLength={200} autoComplete="street-address" className={input} data-testid="address-input" />
        </Field>
        <Field label={t('Phone')} error={issues.get('phone')} group>
          <PhoneInput value={initial.phone} onChange={set('phone')} />
        </Field>
      </Card>

      <Link to="/profile/account" className="mt-6 flex items-center justify-between rounded-2xl bg-ink-850 p-4 font-semibold" data-testid="open-account">
        {t('Email & password')}
        <span className="text-xl text-ink-500">›</span>
      </Link>

      <Button
        className="mt-6 w-full"
        disabled={!dirty || issues.size > 0 || !online}
        loading={save.isPending}
        onClick={() => check?.success && save.mutate(check.data)}
        data-testid="save-profile"
      >
        {t('Save')}
      </Button>
    </Page>
  );
}

function Field({ label, hint, error, group, children }: { label: string; hint?: string; error?: string; group?: boolean; children: ReactNode }) {
  // A field with several controls (phone) must not be one <label>: a tap would always focus the first.
  const Wrap = group ? 'div' : 'label';
  return (
    <Wrap className="block min-w-0">
      <span className="text-sm font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
      {error ? <p className="mt-1 text-xs text-enemy">{error}</p> : hint ? <p className="mt-1 text-right text-xs text-ink-500">{hint}</p> : null}
    </Wrap>
  );
}
